class LiftKitPdpSectionNav extends HTMLElement {
  connectedCallback() {
    this.visibleClasses = ["max-h-[140px]", "opacity-100", "pointer-events-auto", "translate-y-0"];
    this.hiddenClasses = ["max-h-0", "opacity-0", "pointer-events-none", "-translate-y-3"];
    this.activeButtonClasses = ["border-[#1654A3]", "text-[#1654A3]"];
    this.inactiveButtonClasses = ["border-transparent", "text-[#111111]"];
    this.buttons = Array.from(this.querySelectorAll("[data-lk-nav-target]"));
    if (!this.buttons.length) {
      this.hidden = true;
      return;
    }
    this.visibleThreshold = Number(this.dataset.visibleThreshold || 80);
    this.scrollOffset = Number(this.dataset.scrollOffset || 184);
    this.alwaysVisible = this.dataset.alwaysVisible === "true";
    this.useDynamicOffset = this.dataset.dynamicOffset === "true";
    this.mask = this.parentElement.querySelector("[data-lk-nav-mask]");
    this.offsetRoot = this.closest("[data-lk-nav-offset-root]");
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const sectionId = button.dataset.lkNavTarget;
        const section = this.findSection(sectionId);
        if (!section) return;
        window.scrollTo({
          top: this.getScrollTarget(section),
          behavior: "smooth"
        });
      });
    });
    this.refresh();
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize);
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
  }
  onScroll() {
    this.updateVisibility();
    this.updateActiveSection();
  }
  onResize() {
    this.refresh();
  }
  refresh() {
    this.availableSections = this.buttons.map((button) => {
      const id = button.dataset.lkNavTarget;
      const section = this.findSection(id);
      const exists = Boolean(section);
      button.hidden = !exists;
      return exists ? { id, section, button } : null;
    }).filter(Boolean);
    if (!this.availableSections.length) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    this.updateVisibility();
    this.updateActiveSection();
  }
  findSection(sectionId) {
    const sections = Array.from(
      document.querySelectorAll(`[data-lk-nav-section="${sectionId}"]`)
    );
    return sections.find((section) => this.isRenderableSection(section)) || null;
  }
  isRenderableSection(section) {
    if (!section || section.hidden) return false;
    if (section.offsetParent === null) return false;
    if (section.getBoundingClientRect().height < 24) return false;
    return section.textContent.trim().length > 0 || section.children.length > 0;
  }
  getOffset() {
    if (this.useDynamicOffset && this.offsetRoot) {
      return Math.max(0, Math.round(this.offsetRoot.offsetHeight));
    }
    return this.scrollOffset;
  }
  getAbsoluteTop(element) {
    return window.scrollY + element.getBoundingClientRect().top;
  }
  getAbsoluteBottom(element) {
    const rect = element.getBoundingClientRect();
    return window.scrollY + rect.top + rect.height;
  }
  getVisibilityRange() {
    if (!this.availableSections.length) return null;
    const firstSection = this.availableSections[0].section;
    const lastSection = this.availableSections[this.availableSections.length - 1].section;
    const start = Math.max(
      this.visibleThreshold,
      this.getAbsoluteTop(firstSection) - this.getOffset()
    );
    const end = this.getAbsoluteBottom(lastSection) - this.getOffset() - 24;
    return { start, end };
  }
  getScrollTarget(section) {
    const top = window.scrollY + section.getBoundingClientRect().top;
    return Math.max(0, top - this.getOffset());
  }
  updateVisibility() {
    if (this.alwaysVisible) {
      this.classList.remove(...this.hiddenClasses);
      this.classList.add(...this.visibleClasses);
      if (this.mask) {
        this.mask.classList.add("opacity-0");
        this.mask.classList.remove("opacity-100");
      }
      return;
    }
    const range = this.getVisibilityRange();
    const position = window.scrollY + this.getOffset();
    const shouldShow = Boolean(range) && position >= range.start && position <= range.end;
    this.classList.toggle("hidden", false);
    this.classList.remove(...shouldShow ? this.hiddenClasses : this.visibleClasses);
    this.classList.add(...shouldShow ? this.visibleClasses : this.hiddenClasses);
    if (this.mask) {
      this.mask.classList.toggle("opacity-0", !shouldShow);
      this.mask.classList.toggle("opacity-100", shouldShow);
    }
  }
  updateActiveSection() {
    if (!this.availableSections.length) return;
    const position = window.scrollY + this.getOffset() + 24;
    let activeId = this.availableSections[0].id;
    this.availableSections.forEach(({ id, section }) => {
      const sectionTop = window.scrollY + section.getBoundingClientRect().top;
      if (position >= sectionTop) {
        activeId = id;
      }
    });
    this.buttons.forEach((button) => {
      const isActive = button.dataset.lkNavTarget === activeId;
      button.classList.remove(...isActive ? this.inactiveButtonClasses : this.activeButtonClasses);
      button.classList.add(...isActive ? this.activeButtonClasses : this.inactiveButtonClasses);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }
}
if (!customElements.get("lift-kit-pdp-section-nav")) {
  customElements.define("lift-kit-pdp-section-nav", LiftKitPdpSectionNav);
}
